import produce from 'immer';
import _ from 'lodash';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useLazyQuery} from 'react-apollo';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';

import {
  ArtifactSequence,
  FetchProjectJobsForTriggersPartialDocument,
  FetchProjectJobsForTriggersPartialQuery,
  FetchProjectJobsForTriggersPartialQueryResult,
  PageInfo,
  RunQueueAccessType,
  RunQueuePrioritizationMode,
  TriggeredActionConfig,
  TriggeredActionType as GQLTriggeredActionType,
  TriggerScopeType,
  useCreateDefaultResourceConfigMutation,
  useCreateFilterTriggerMutation,
  useCreateRunQueueMutation,
  useEntityArtifactTypeQuery,
  useUpsertModelMutation,
} from '../../../generated/graphql';
import {propagateErrorsContext} from '../../../util/errors';
import {runRegexFilter, toMongo, usernameFilter} from '../../../util/filters';
import {Filter, FilterKey} from '../../../util/filterTypes';
import {DEFAULT_LAUNCH_PROJECT_NAME} from '../constants';
import {getJobDetails} from '../JobDetail/hooks';
import {JobDetails} from '../JobDetail/utilTypes';
import {TriggerRootFilter} from './CreateTriggerDrawer/EventStore';
import {RunFilter} from './CreateTriggerDrawer/RunFilterShared';
import {MetricChangeFilter} from './CreateTriggerDrawer/RunMetricChangeSection';
import {
  getNumberValueForField,
  MetricFilter,
} from './CreateTriggerDrawer/RunMetricThresholdSection';
import {RunStateFilter} from './CreateTriggerDrawer/RunStateSection';
import {JobOption} from './CreateTriggerDrawer/types';
import {EventTriggeringConditionType} from './TriggerEventTypes';

// Same as getJobOptions() from ../Launch/hooks.ts
// But has a slightly different value type (JobDetail) to
// handle the extra information needed per Job
const getJobOptions = (
  data: FetchProjectJobsForTriggersPartialQuery
): JobOption[] => {
  const options = [];
  const collectionEdges =
    data?.project?.artifactType?.artifactCollections?.edges ?? [];
  for (const collectionEdge of collectionEdges) {
    // Can only run a Job if it has versions associated with it.
    // It might not if it was created through the Artifacts UI.
    const artifactCollection = collectionEdge.node;
    const versionEdges = artifactCollection?.artifacts?.edges ?? [];
    if (versionEdges.length) {
      const jobDetails = getJobDetails(artifactCollection as ArtifactSequence);
      if (jobDetails != null) {
        options.push({
          value: jobDetails,
          label: artifactCollection?.name ?? '',
        });
      }
    }
  }
  return _.sortBy(options, 'label');
};

// Same as useAvailableProjectJobOptions() from ../Launch/hooks.ts
// But skips fetching EntityJobs and gets project jobs with
// a slightly different query that fetches extra data per job
export const useAvailableProjectJobOptions = (
  entityName: string,
  projectName: string
): {jobOptions: JobOption[]; loading: boolean} => {
  const projectJobsQuery = useFetchProjectJobsForTriggersIncrementally(
    entityName,
    projectName
  );
  const noResults = projectJobsQuery.loading || projectJobsQuery.error;
  const projectData = projectJobsQuery.data;

  return useMemo(() => {
    if (noResults) {
      return {jobOptions: [] as JobOption[], loading: true};
    }
    const jobOptions = getJobOptions(projectData!);
    return {
      jobOptions,
      loading: false,
    };
  }, [noResults, projectData]);
};

const INCREMENTAL_BATCH_SIZE = 20;
const INCREMENTAL_MAX_RETRIES = 10;
const INCREMENTAL_MAX_EBO_SCALE = 6;
export type FetchProjectJobsForTriggersResult = {
  data: FetchProjectJobsForTriggersPartialQueryResult['data'];
  error: Error | null;
  loading: boolean; // Are we loading initial data?
  loadingMore: boolean; // Are we loading more data?
  refetch: () => void;
};

// Same as useFetchProjectJobsIncrementally() from ../Launch/hooks.ts
// But uses a slightly different query that fetches more data per job
export const useFetchProjectJobsForTriggersIncrementally = (
  entityName: string,
  projectName: string
): FetchProjectJobsForTriggersResult => {
  const lastPageInfo = useRef<PageInfo | undefined>(undefined);
  const retries = useRef(0);
  const mounted = useRef(true);

  const [result, setResult] =
    useState<FetchProjectJobsForTriggersResult['data']>(undefined);
  const [err, setErr] = useState<Error | null>(null);

  const [fetchJobsIncrementally, {loading: loadingMore}] = useLazyQuery(
    FetchProjectJobsForTriggersPartialDocument,
    {
      onError: e => {
        if (!mounted.current) {
          return;
        }

        if (e.networkError == null) {
          // Treat gql errors as fatal
          setErr(e);
          return;
        }

        // On net error only, retry with exponential backoff, up to 64 seconds/retry
        retries.current++;
        if (retries.current > INCREMENTAL_MAX_RETRIES) {
          setErr(
            new Error(
              `Max retries exceeded (${retries.current}). Last error: ${e.networkError.message}`
            )
          );
          return;
        }

        setTimeout(
          () => {
            if (!mounted.current) {
              return;
            }
            fetchJobsIncrementally({
              variables: {
                entityName,
                projectName,
                limit: INCREMENTAL_BATCH_SIZE,
                after: lastPageInfo.current?.endCursor ?? '',
              },
            });
          },
          1000 * 2 ** Math.min(retries.current, INCREMENTAL_MAX_EBO_SCALE)
        );
      },
      onCompleted: data => {
        if (result != null && !mounted.current) {
          return;
        }

        retries.current = 0;
        const newItems =
          data.project?.artifactType?.artifactCollections?.edges ?? [];
        setResult(old => {
          // First result, return the data as usual
          if (old == null) {
            return data;
          }

          // Subsequent results, merge the new collections into existing data
          return produce(old, draft => {
            draft?.project?.artifactType?.artifactCollections?.edges.push(
              ...newItems
            );
          });
        });
        lastPageInfo.current =
          data.project?.artifactType?.artifactCollections.pageInfo;
        if (lastPageInfo.current?.hasNextPage) {
          fetchJobsIncrementally({
            variables: {
              entityName,
              projectName,
              limit: INCREMENTAL_BATCH_SIZE,
              after: lastPageInfo.current?.endCursor ?? '',
            },
          });
        }
      },
      variables: {
        entityName,
        projectName,
        limit: INCREMENTAL_BATCH_SIZE,
        after: '',
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const fetchFresh = () => {
    setResult(undefined);
    fetchJobsIncrementally();
  };

  // only run on first mount or project changes
  useEffect(() => {
    fetchFresh();
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName, projectName]);

  return {
    error: err,
    data: result,
    loading: result == null,
    loadingMore,
    refetch: fetchFresh,
  };
};

// Derive job details from list of job artifact sequences
// from the gql result
export function useEntityHasJobs(entityName: string | undefined) {
  const {
    data: entityJobArtifacts,
    loading,
    error,
  } = useEntityArtifactTypeQuery({
    variables: {
      entityName: entityName ?? '' /* ?'' valid because of skip below */,
      artifactType: 'job',
    },
    skip: entityName == null,
  });

  const projects = entityJobArtifacts?.entity?.projects;
  let entityHasJobArtifacts = false;
  projects?.edges.forEach(project => {
    const artifactType = project?.node?.artifactType;
    if (artifactType != null) {
      entityHasJobArtifacts = true;
    }
  });

  if (projects == null || loading || error) {
    return {data: false, loading, error};
  }
  return {
    data: entityHasJobArtifacts,
    loading: false,
    error: null,
  };
}

export function useCreateQueue() {
  const [upsertProject] = useUpsertModelMutation({
    context: propagateErrorsContext(),
  });
  const [createDefaultResourceConfig] = useCreateDefaultResourceConfigMutation({
    context: propagateErrorsContext(),
  });
  const [createRunQueue] = useCreateRunQueueMutation({
    context: propagateErrorsContext(),
  });
  const [loading, setLoading] = useState(false);

  async function createQueue(
    entityName: string,
    targetProject: string,
    queueName: string
  ) {
    if (entityName === '' || targetProject === '' || queueName === '') {
      return;
    }
    let queueID: string;
    setLoading(true);
    try {
      // Create a starter queue
      // First, ensure we have the necessary project for an entity-scoped queue.
      await upsertProject({
        variables: {
          entityName,
          name: DEFAULT_LAUNCH_PROJECT_NAME,
        },
      });

      const completeConfig = {
        resource_args: {
          'local-container': {},
        },
      };
      const createConfigResponse = await createDefaultResourceConfig({
        variables: {
          entityName,
          resource: 'local-container',
          config: JSON.stringify(completeConfig),
          // default config shouldn't have any template variables
          templateVariables: '{}',
        },
      });

      const defaultResourceConfigID =
        createConfigResponse.data?.createDefaultResourceConfig
          ?.defaultResourceConfigID;

      // Prioritization is hardcoded disabled: Not supported for triggers
      const createQueueResponse = await createRunQueue({
        variables: {
          entityName,
          projectName: DEFAULT_LAUNCH_PROJECT_NAME,
          queueName,
          access: RunQueueAccessType.Project,
          defaultResourceConfigID,
          prioritizationMode: RunQueuePrioritizationMode.Disabled,
        },
      });
      queueID = createQueueResponse.data?.createRunQueue?.queueID ?? '';
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error('Error creating automation: unable to create entity queue.');
      setLoading(false);
      return undefined;
    }
    return queueID;
  }
  return {createQueue, loading};
}

export function useCreateAutomation() {
  const [createTriggerMutation] = useCreateFilterTriggerMutation({
    context: propagateErrorsContext(),
  });
  const [loading, setLoading] = useState(false);
  async function createQueuedJobAutomation(
    entityName: string,
    queuedJob: JobDetails | undefined | null,
    triggerScopeType: TriggerScopeType | undefined,
    triggerScopeID: string | undefined,
    eventTriggeringConditionType: EventTriggeringConditionType | undefined,
    filters: TriggerRootFilter,
    queuedJobAlias: string | undefined | null,
    queueID: string,
    targetProject: string,
    overrides: string,
    triggerName: string,
    triggerDescription: string,
    defaultTriggerDescription: string
  ) {
    if (
      queuedJob == null ||
      triggerScopeType == null ||
      triggerScopeID == null ||
      eventTriggeringConditionType == null ||
      filters == null ||
      queuedJobAlias == null ||
      queueID === ''
    ) {
      console.error('Failed to create job trigger - invalid starting config', {
        queuedJob,
        triggerScopeType,
        triggerScopeID,
        eventTriggeringConditionType,
        filters,
        queuedJobAlias,
        queueID,
      });
      toast.error(
        'Something went wrong while trying to create this automation'
      );
      return;
    }

    setLoading(true);
    const triggeredActionConfig = {
      queueJobActionInput: {
        queueID,
        template: JSON.stringify(
          {
            job: `${entityName}/${queuedJob?.projectName}/${queuedJob?.name}:${queuedJobAlias}`,
            entity: entityName,
            project: targetProject,
            overrides: JSON.parse(overrides),
          },
          null,
          2
        ),
      },
    };

    let createdTrigger;
    try {
      createdTrigger = await createTriggerMutation({
        variables: {
          name: triggerName,
          description:
            triggerDescription !== ''
              ? triggerDescription
              : defaultTriggerDescription,
          triggeringEventType: eventTriggeringConditionType,
          scopeType: triggerScopeType,
          scopeID: triggerScopeID,
          eventFilter: JSON.stringify(toMongo(filters)),
          triggeredActionType: GQLTriggeredActionType.QueueJob,
          triggeredActionConfig,
          enabled: true,
        },
      });
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error(
        'Something went wrong while trying to create this automation'
      );
      setLoading(false);
      return undefined;
    }
    return createdTrigger;
  }

  async function createWebhookAutomation(
    triggerScopeType: TriggerScopeType | undefined,
    triggerScopeID: string | undefined,
    eventTriggeringConditionType: EventTriggeringConditionType | undefined,
    filters: TriggerRootFilter,
    webhookID: string,
    webhookPayload: string,
    triggerName: string,
    triggerDescription: string,
    defaultTriggerDescription: string
  ) {
    if (
      triggerScopeType == null ||
      triggerScopeID == null ||
      eventTriggeringConditionType == null ||
      filters == null ||
      webhookID === ''
    ) {
      console.error(
        'Failed to create webhook trigger - invalid starting config',
        {
          triggerScopeType,
          triggerScopeID,
          eventTriggeringConditionType,
          filters,
          webhookID,
        }
      );
      toast.error(
        'Something went wrong while trying to create this automation'
      );
      return;
    }

    const {triggeredActionConfig} = createTriggerActionConfigForWebhook(
      webhookID,
      webhookPayload
    );

    let createdTrigger;
    setLoading(true);
    try {
      createdTrigger = await createTriggerMutation({
        variables: {
          name: triggerName,
          description:
            triggerDescription !== ''
              ? triggerDescription
              : defaultTriggerDescription,
          triggeringEventType: eventTriggeringConditionType,
          scopeType: triggerScopeType,
          scopeID: triggerScopeID,
          eventFilter: JSON.stringify(toMongo(filters)),
          triggeredActionType: GQLTriggeredActionType.GenericWebhook,
          triggeredActionConfig,
          enabled: true,
        },
      });
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error(
        'Something went wrong while trying to create this automation'
      );
      setLoading(false);
      return undefined;
    }
    return createdTrigger;
  }

  const createTriggerForEventAndAction = async (
    triggerEventConfig: TriggerEventConfig,
    triggerActionConfig: TriggerActionConfig,
    triggerName: string,
    triggerDescription: string,
    defaultTriggerDescription: string,
    enabled: boolean
  ) => {
    setLoading(true);
    try {
      return await createTriggerMutation({
        variables: {
          name: triggerName,
          description:
            triggerDescription !== ''
              ? triggerDescription
              : defaultTriggerDescription,
          ...triggerEventConfig,
          ...triggerActionConfig,
          enabled,
        },
      });
    } catch (error) {
      console.error(error);
      setLoading(false);
      return undefined;
    }
  };
  return {
    createQueuedJobAutomation,
    createWebhookAutomation,
    createTriggerForEventAndAction,
    loading,
  };
}

const getRunFilterAsMongoFilter = (runFilter: RunFilter): Filter<FilterKey> => {
  const filters: Filter<FilterKey>[] = [];
  if (runFilter.isRunNameFilterEnabled) {
    filters.push(runRegexFilter(runFilter.runName!, 'run', 'displayName'));
  }
  if (runFilter.isUsernameFilterEnabled) {
    filters.push(usernameFilter(runFilter.username!));
  }
  return toMongo({op: 'AND', filters});
};

export const createTriggerEventConfigForRunMetric = (
  projectId: string,
  runFilter: RunFilter,
  metricFilter: MetricFilter
): TriggerEventConfig => {
  return {
    triggeringEventType: EventTriggeringConditionType.RunMetric,
    scopeType: TriggerScopeType.Project,
    scopeID: projectId,
    eventFilter: JSON.stringify({
      run_filter: JSON.stringify(getRunFilterAsMongoFilter(runFilter)),
      run_metric_filter: {
        threshold_filter: {
          name: metricFilter.metricName,
          threshold: getNumberValueForField(metricFilter.threshold),
          window_size: getNumberValueForField(metricFilter.windowSize),
          cmp_op: metricFilter.cmpOp.value,
          agg_op: metricFilter.aggOp,
        },
        panel_link: metricFilter.panelLink,
      },
    }),
  };
};

export const createTriggerEventConfigForRunMetricChange = (
  projectId: string,
  runFilter: RunFilter,
  metricChangeFilter: MetricChangeFilter
): TriggerEventConfig => {
  let numChangeAmount = Number(metricChangeFilter.changeAmount);
  if (metricChangeFilter.changeType === 'RELATIVE') {
    numChangeAmount = numChangeAmount / 100;
  }
  return {
    triggeringEventType: EventTriggeringConditionType.RunMetricChange,
    scopeType: TriggerScopeType.Project,
    scopeID: projectId,
    eventFilter: JSON.stringify({
      run_filter: JSON.stringify(getRunFilterAsMongoFilter(runFilter)),
      run_metric_filter: {
        change_filter: {
          name: metricChangeFilter.metricName,
          prior_window_size: getNumberValueForField(
            metricChangeFilter.priorSize
          ),
          current_window_size: getNumberValueForField(
            metricChangeFilter.currentSize
          ),
          agg_op: metricChangeFilter.aggOp,
          change_amount: numChangeAmount,
          change_type: metricChangeFilter.changeType,
          change_dir: metricChangeFilter.changeDirection,
        },
        panel_link: metricChangeFilter.panelLink,
      },
    }),
  };
};

export const createTriggerEventConfigForRunState = (
  projectId: string,
  runFilter: RunFilter,
  runStateFilter: RunStateFilter
): TriggerEventConfig => {
  return {
    triggeringEventType: EventTriggeringConditionType.RunState,
    scopeType: TriggerScopeType.Project,
    scopeID: projectId,
    eventFilter: JSON.stringify({
      run_filter: JSON.stringify(getRunFilterAsMongoFilter(runFilter)),
      run_state_filter: {
        states: runStateFilter.states,
      },
    }),
  };
};

export const createTriggerActionConfigForSlack = (
  slackIntegrationID: string
): TriggerActionConfig => {
  return {
    triggeredActionType: GQLTriggeredActionType.Notification,
    triggeredActionConfig: {
      notificationActionInput: {
        integrationID: slackIntegrationID,
      },
    },
  };
};

export const createTriggerActionConfigForWebhook = (
  webhookID: string,
  webhookPayload: string
): TriggerActionConfig => ({
  triggeredActionType: GQLTriggeredActionType.GenericWebhook,
  triggeredActionConfig: {
    genericWebhookActionInput: {
      integrationID: webhookID,
      requestPayload: webhookPayload,
    },
  },
});

export interface TriggerEventConfig {
  triggeringEventType: EventTriggeringConditionType;
  scopeType: TriggerScopeType;
  scopeID: string;
  eventFilter: string;
}

export interface TriggerActionConfig {
  triggeredActionType: GQLTriggeredActionType;
  triggeredActionConfig: TriggeredActionConfig;
}
